import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import ChatGPTMappingEditor from './ChatGPTMappingEditor';

const EMPTY_TEMPLATE = {
    name: '',
    description: '',
    gptUrl: '',
    sceneCount: 1,
    promptTemplate: 'Product Name: {productName}\nProduct Link: {productLink}\n\nKeluarkan output dalam format JSON yang diminta.',
    mapping: '',
};

const SmartCustomChatGPTTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_TEMPLATE);
    const [showForm, setShowForm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    useEffect(() => {
        const ref = collection(db, 'system', 'chatgptTemplates', 'items');
        const unsub = onSnapshot(ref, (snap) => {
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setTemplates(items);
            setIsLoading(false);
        }, (err) => {
            console.error('System templates listener error:', err.code, err.message);
            setIsLoading(false);
        });
        return () => unsub();
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_TEMPLATE });
        setShowForm(true);
    };

    const openEdit = (tpl) => {
        setEditingId(tpl.id);
        setForm({
            name: tpl.name || '',
            description: tpl.description || '',
            gptUrl: tpl.gptUrl || '',
            sceneCount: tpl.sceneCount || 1,
            promptTemplate: tpl.promptTemplate || EMPTY_TEMPLATE.promptTemplate,
            mapping: typeof tpl.mapping === 'string' ? tpl.mapping : JSON.stringify(tpl.mapping || {}, null, 2),
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(EMPTY_TEMPLATE);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { alert('Name wajib diisi'); return; }
        if (!form.gptUrl.trim()) { alert('GPT URL wajib diisi'); return; }

        if (form.mapping.trim()) {
            try { JSON.parse(form.mapping); }
            catch (e) { alert('Mapping bukan JSON valid: ' + e.message); return; }
        }

        setIsSaving(true);
        try {
            const id = editingId || `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const ref = doc(db, 'system', 'chatgptTemplates', 'items', id);
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                gptUrl: form.gptUrl.trim(),
                sceneCount: Number(form.sceneCount) === 2 ? 2 : 1,
                promptTemplate: form.promptTemplate,
                mapping: form.mapping.trim(),
                updatedAt: serverTimestamp(),
            };
            if (!editingId) payload.createdAt = serverTimestamp();
            await setDoc(ref, payload, { merge: true });
            closeForm();
        } catch (e) {
            console.error('System template save failed:', e.code, e.message);
            alert('Save gagal: ' + e.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, 'system', 'chatgptTemplates', 'items', id));
            setShowDeleteConfirm(null);
        } catch (e) {
            console.error('Delete failed:', e);
            alert('Delete gagal: ' + e.message);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center">
                        <span className="material-symbols-outlined text-lg text-violet-500">smart_toy</span>
                    </div>
                    <div>
                        <h3 className="font-bold text-sm text-slate-900 dark:text-white">ChatGPT System Templates</h3>
                        <p className="text-[10px] text-slate-400">Preset Custom GPT untuk semua user SMART — boleh diclone</p>
                    </div>
                </div>
                <button
                    onClick={openCreate}
                    className="px-3 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-[11px] font-bold flex items-center gap-1 shadow-sm"
                >
                    <span className="material-symbols-outlined text-[14px]">add</span>
                    New Template
                </button>
            </div>

            {isLoading ? (
                <div className="text-center py-8 text-[11px] text-slate-400">Loading templates...</div>
            ) : templates.length === 0 ? (
                <div className="text-center py-8">
                    <span className="material-symbols-outlined text-3xl text-slate-300 mb-2">smart_toy</span>
                    <p className="text-[11px] text-slate-500">Belum ada system template. Click "New Template" untuk add.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {templates.map(tpl => (
                        <div key={tpl.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <p className="text-[12px] font-bold text-slate-700 dark:text-slate-200 truncate">{tpl.name}</p>
                                    <span className="text-[8px] px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded uppercase tracking-wider font-bold">
                                        {tpl.sceneCount === 2 ? '2 Scene' : '1 Scene'}
                                    </span>
                                </div>
                                {tpl.description && (
                                    <p className="text-[10px] text-slate-400 truncate mb-0.5">{tpl.description}</p>
                                )}
                                <p className="text-[9px] text-slate-400 truncate font-mono">{tpl.gptUrl}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                                <button
                                    onClick={() => openEdit(tpl)}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-violet-500 transition-colors"
                                    title="Edit"
                                >
                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                </button>
                                <button
                                    onClick={() => setShowDeleteConfirm(tpl.id)}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-rose-500 transition-colors"
                                    title="Delete"
                                >
                                    <span className="material-symbols-outlined text-[16px]">delete</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Form Modal */}
            {showForm && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
                    <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-lg text-violet-500">smart_toy</span>
                                <h3 className="text-sm font-bold text-slate-700 dark:text-white">
                                    {editingId ? 'Edit System Template' : 'New System Template'}
                                </h3>
                            </div>
                            <button onClick={closeForm} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                                <span className="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>

                        <div className="flex-1 p-5 overflow-auto space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="cth: AI8SAAT JSON GPT"
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Description</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Untuk apa template ini"
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">GPT URL *</label>
                                <input
                                    type="text"
                                    value={form.gptUrl}
                                    onChange={(e) => setForm({ ...form, gptUrl: e.target.value })}
                                    placeholder="https://chatgpt.com/g/g-xxx-name"
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5 font-mono"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Scene Count</label>
                                <div className="flex gap-2">
                                    {[1, 2].map(n => (
                                        <button
                                            key={n}
                                            onClick={() => setForm({ ...form, sceneCount: n })}
                                            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${form.sceneCount === n
                                                ? 'bg-violet-500 text-white shadow-sm'
                                                : 'bg-slate-50 dark:bg-slate-800 text-slate-500 hover:bg-slate-100'
                                                }`}
                                        >
                                            {n} Scene
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Prompt Template</label>
                                <p className="text-[9px] text-slate-400 mb-1.5">Placeholder: {`{productName}, {productLink}, {imageUrl}`}</p>
                                <textarea
                                    value={form.promptTemplate}
                                    onChange={(e) => setForm({ ...form, promptTemplate: e.target.value })}
                                    rows={5}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs p-2.5 font-mono resize-y"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Mapping (optional)</label>
                                <ChatGPTMappingEditor
                                    value={form.mapping}
                                    onChange={(val) => setForm({ ...form, mapping: val })}
                                    sceneCount={form.sceneCount}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                            <button
                                onClick={closeForm}
                                disabled={isSaving}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-500 hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white text-[11px] font-bold shadow-sm flex items-center gap-1 disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-[13px]">save</span>
                                        {editingId ? 'Update' : 'Create'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-2">Delete template?</h4>
                        <p className="text-[11px] text-slate-500 mb-4">User yang dah clone tak terjejas. Tapi template asal akan hilang dari list system.</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowDeleteConfirm(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
                            <button onClick={() => handleDelete(showDeleteConfirm)} className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-bold">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SmartCustomChatGPTTemplates;
